import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { LabelStorageRule } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { ItemPicker } from '@/components/ItemPicker'
import { PurchasePartnerCombo } from '@/components/PurchasePartnerCombo'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'

const API = '/api/purchase/label-storage-rules'
const QK = 'label-storage-rules'
const PRESETS = ['냉장 0℃ ~ 10℃ 이하', '냉동보관', '실온보관', '냉장 및 실온보관']

interface RuleForm {
  id?: number
  group: number | null
  partner: number | null
  scope_label: string
  item: number | null
  item_label: string
  storage: string
}

const blank: RuleForm = { group: null, partner: null, scope_label: '', item: null, item_label: '', storage: '실온보관' }

/** 회사관리 > 라벨 > 보관방법 — 라벨 보관방법 규칙(전사통합). */
export function LabelStorageRuleTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<RuleForm | null>(null)

  const { data: rows = [], isFetching } = useQuery({
    queryKey: [QK, search],
    queryFn: () => api<LabelStorageRule[]>(`${API}${search ? `?q=${encodeURIComponent(search)}` : ''}`),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`${API}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [QK] }); push('삭제됨', 'success') },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: RuleForm) => {
      const body = { group: f.group, partner: f.partner, item: f.item, storage: f.storage.trim() }
      return f.id ? api(`${API}/${f.id}`, { method: 'PATCH', body }) : api(API, { method: 'POST', body })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [QK] }); setEdit(null); push('저장됨', 'success') },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  function openEdit(r: LabelStorageRule) {
    setEdit({
      id: r.id, group: r.group, partner: r.partner,
      scope_label: r.group_name ? `[상위] ${r.group_name}` : (r.partner_name ?? ''),
      item: r.item, item_label: r.item ? `${r.item_code} ${r.item_name}` : '',
      storage: r.storage,
    })
  }

  const hasScope = !!edit && (!!edit.group || !!edit.partner)
  const canSubmit = !!edit && (hasScope || !!edit.item) && !!edit.storage.trim()

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-3 text-xs text-slate-500 leading-relaxed mb-3">
        규칙이 없으면 <b>냉장 0℃ ~ 10℃ 이하</b>, 품명에 <b>냉동</b>이 있으면 <b>냉동보관</b>입니다.
        적용 순서: 거래처+품목 → 상위거래처+품목 → 품목(전체) → 품명 '냉동' → 거래처 → 상위거래처 → 기본.
        (거래처·상위거래처만 지정한 규칙은 냉동 품목에는 적용되지 않습니다.)
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="거래처 / 상위거래처 / 품목 / 보관방법 검색"
               className={inputCls + ' flex-1 min-w-[220px]'}/>
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={() => setEdit({ ...blank })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 규칙 추가
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-28">회사</th>
              <th className="px-3 py-2.5 text-left font-medium">범위</th>
              <th className="px-3 py-2.5 text-left font-medium">품목</th>
              <th className="px-3 py-2.5 text-left font-medium w-44">보관방법</th>
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5 text-xs text-slate-400">{r.company_name ?? ''}</td>
                <td className="px-3 py-1.5 text-slate-800">
                  {r.group_name ? (
                    <><span className="text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 mr-1">상위</span>{r.group_name}</>
                  ) : r.partner_name ?? <span className="text-xs text-slate-400">전체 거래처</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-700">
                  {r.item_name ? (
                    <><span className="font-mono text-xs text-slate-400 mr-1">{r.item_code}</span>{r.item_name}</>
                  ) : <span className="text-xs text-slate-400">전 품목</span>}
                </td>
                <td className="px-3 py-1.5 font-medium text-slate-900">{r.storage}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-blue-600 hover:text-blue-800 mr-2"><Pencil className="w-3.5 h-3.5"/></button>
                  <button onClick={() => { void confirm('이 보관방법 규칙을 삭제할까요?', { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                </td>
              </tr>
            ))}
            {!rows.length && !isFetching && (
              <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-400">
                규칙이 없습니다. 우측 상단 "규칙 추가"로 시작하세요.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? '보관방법 규칙 수정' : '보관방법 규칙 추가'} size="lg">
        {edit && (
          <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }} className="space-y-3">
            <Field label="범위 (상위거래처 또는 거래처, 비우면 전체 거래처)" full>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PurchasePartnerCombo withGroups label={edit.scope_label} placeholder="전체 거래처 (검색해서 지정)"
                    onChange={(h) => setEdit({
                      ...edit,
                      group: h.is_group ? h.id : null,
                      partner: h.is_group ? null : h.id,
                      scope_label: h.is_group ? `[상위] ${h.name}` : h.name,
                    })}/>
                </div>
                {hasScope && (
                  <button type="button" onClick={() => setEdit({ ...edit, group: null, partner: null, scope_label: '' })}
                          className="text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded-lg px-2 py-1.5 whitespace-nowrap">
                    전체로
                  </button>
                )}
              </div>
            </Field>
            <Field label="품목 (비우면 범위 안 전 품목)" full>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <ItemPicker value={edit.item} initialLabel={edit.item_label} placeholder="전 품목"
                    onChange={(it) => setEdit({ ...edit, item: it.id, item_label: `${it.code} ${it.name}` })}/>
                </div>
                {edit.item && (
                  <button type="button" onClick={() => setEdit({ ...edit, item: null, item_label: '' })}
                          className="text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded-lg px-2 py-1.5 whitespace-nowrap">
                    전 품목으로
                  </button>
                )}
              </div>
            </Field>
            <Field label="보관방법 *" full>
              <input value={edit.storage} onChange={(e) => setEdit({ ...edit, storage: e.target.value })}
                     list="label-storage-presets" className={inputCls}/>
              <datalist id="label-storage-presets">
                {PRESETS.map((p) => <option key={p} value={p}/>)}
              </datalist>
              <div className="flex gap-1 mt-1.5 flex-wrap">
                {PRESETS.map((p) => (
                  <button key={p} type="button" onClick={() => setEdit({ ...edit, storage: p })}
                          className={cn('text-[11px] rounded border px-1.5 py-0.5',
                            edit.storage === p ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'border-slate-300 text-slate-500 hover:bg-slate-50')}>
                    {p}
                  </button>
                ))}
              </div>
            </Field>
            {!hasScope && !edit.item && (
              <div className="text-xs text-amber-600">범위나 품목 중 하나는 지정해야 합니다.</div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!canSubmit || save.isPending}
                      className={cn('px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white')}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
